import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { useEEGAnalysis, useConnectionState } from '../../../stores/processedDataStore';
import { useSensorContactStatus } from '../../../stores/systemStore';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from "../../../locales";
import { useLanguageStore } from "../../../stores/languageStore";

const PowerSpectrumChart: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const eegAnalysis = useEEGAnalysis();
  const isConnected = useConnectionState();
  const { isSensorContacted, leadOffStatus } = useSensorContactStatus();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!chartRef.current) return;

    // 차트 초기화
    chartInstance.current = echarts.init(chartRef.current);
    
    const option: echarts.EChartsOption = {
      title: {
        text: t.visualizer.powerSpectrum,
        left: 'center',
        textStyle: {
          fontSize: 14,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const freq = params[0].value[0];
          let result = `주파수: ${freq.toFixed(1)} Hz<br/>`;
          params.forEach((param: any) => {
            result += `${param.seriesName}: ${param.value[1].toFixed(2)} dB<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['FP1 (Ch1)', 'FP2 (Ch2)'],
        top: 30
      },
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '15%'
      },
      xAxis: {
        type: 'value',
        name: t.visualizer.xAxisFrequency,
        nameLocation: 'middle',
        nameGap: 25,
        min: 1,
        max: 45,
        axisLabel: {
          formatter: '{value} Hz'
        }
      },
      yAxis: {
        type: 'value',
        name: t.visualizer.yAxisPower,
        nameLocation: 'middle',
        nameGap: 50,
        min: 0,
        max: 85,
        axisLabel: {
          formatter: (value: number) => {
            return value.toFixed(1) + ' dB';
          }
        }
      },
      series: [
        {
          name: 'FP1 (Ch1)',
          type: 'line',
          data: [],
          lineStyle: {
            color: '#3b82f6',
            width: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
                { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
              ]
            }
          },
          symbol: 'none',
          animation: false,
          smooth: true
        },
        {
          name: 'FP2 (Ch2)',
          type: 'line',
          data: [],
          lineStyle: {
            color: '#ef4444',
            width: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
                { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
              ]
            }
          },
          symbol: 'none',
          animation: false,
          smooth: true
        }
      ],
      // 주파수 대역 표시
      markArea: {
        silent: true,
        data: [
          [
            { name: 'Delta', xAxis: 1, itemStyle: { color: 'rgba(139, 69, 19, 0.1)' } },
            { xAxis: 4 }
          ],
          [
            { name: 'Theta', xAxis: 4, itemStyle: { color: 'rgba(255, 140, 0, 0.1)' } },
            { xAxis: 8 }
          ],
          [
            { name: 'Alpha', xAxis: 8, itemStyle: { color: 'rgba(50, 205, 50, 0.1)' } },
            { xAxis: 13 }
          ],
          [
            { name: 'Beta', xAxis: 13, itemStyle: { color: 'rgba(30, 144, 255, 0.1)' } },
            { xAxis: 30 }
          ],
          [
            { name: 'Gamma', xAxis: 30, itemStyle: { color: 'rgba(148, 0, 211, 0.1)' } },
            { xAxis: 45 }
          ]
        ]
      }
    };

    chartInstance.current.setOption(option);
    setIsInitialized(true);

    // 리사이즈 핸들러
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, []);

  // EEG 분석 결과 기반 주파수 스펙트럼 업데이트
  useEffect(() => {
    if (!chartInstance.current || !isInitialized) {
      return;
    }

    const updateChart = () => {
      if (!eegAnalysis?.frequencySpectrum?.frequencies || 
          !eegAnalysis?.frequencySpectrum?.ch1Power || 
          !eegAnalysis?.frequencySpectrum?.ch2Power) {
        return;
      }

      const { frequencies, ch1Power, ch2Power } = eegAnalysis.frequencySpectrum;
      
      // 안전한 데이터 검증
      if (!Array.isArray(frequencies) || !Array.isArray(ch1Power) || !Array.isArray(ch2Power) || 
          frequencies.length !== ch1Power.length || frequencies.length !== ch2Power.length) {
        return;
      }

      // ch1, ch2 모두에 대한 스펙트럼 데이터 생성
      const ch1SpectrumData = frequencies.map((freq: number, i: number) => [freq, ch1Power[i]]);
      const ch2SpectrumData = frequencies.map((freq: number, i: number) => [freq, ch2Power[i]]);
      
      chartInstance.current?.setOption({
        series: [
          {
            name: 'FP1 (Ch1)',
            data: ch1SpectrumData,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#3b82f6',
              width: 2
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
                  { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
                ]
              }
            },
            symbol: 'none',
            animation: false
          },
          {
            name: 'FP2 (Ch2)',
            data: ch2SpectrumData,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#ef4444',
              width: 2
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
                  { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
                ]
              }
            },
            symbol: 'none',
            animation: false
          }
        ]
      });
    };

    updateChart();

  }, [isInitialized, eegAnalysis?.frequencySpectrum?.frequencies, eegAnalysis?.frequencySpectrum?.ch1Power, eegAnalysis?.frequencySpectrum?.ch2Power]);

  // 차트 div — 항상 DOM에 존재 (ECharts 초기화를 위해)
  // 데이터 없으면 overlay로 EmptyState 표시
  const showEmptyState = !isConnected || !eegAnalysis?.frequencySpectrum?.frequencies || 
    !eegAnalysis?.frequencySpectrum?.ch1Power || !eegAnalysis?.frequencySpectrum?.ch2Power;

  return (
    <div className="w-full relative">
      {/* 센서 접촉 상태 경고 */}
      {isConnected && !isSensorContacted && (
        <div className="mb-3 p-2 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <span className="text-red-300 text-sm">
            {t.visualizer.poorContact} (FP1: {leadOffStatus.fp1 ? t.visualizer.contactPoor : t.visualizer.contactNormal}, FP2: {leadOffStatus.fp2 ? t.visualizer.contactPoor : t.visualizer.contactNormal}) - 
            {t.visualizer.signalQualityMayDegrade}
          </span>
        </div>
      )}
      
      <div className="relative" style={{ minHeight: '320px' }}>
        <div 
          ref={chartRef} 
          className="w-full h-80"
          style={{ minHeight: '320px' }}
        />
        
        {/* 데이터 없을 때 overlay EmptyState */}
        {showEmptyState && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
            <div className="text-center space-y-4">
              
              <div className="text-lg text-foreground">
                {t.visualizer.noGraphData}
              </div>
              <div className="text-sm text-muted-foreground">
                {t.visualizer.connectDevice}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {t.visualizer.powerSpectrumConnect}
              </div>
              <div className="text-xs text-muted-foreground mt-3 p-2 bg-card rounded">
                {t.visualizer.powerSpectrumAnalyzing}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-2 text-xs text-muted-foreground text-center">
        {t.visualizer.powerSpectrumFooter}
      </div>
      <div className="mt-2 flex justify-center space-x-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-indigo-500 rounded-full mr-1"></div>
          <span className="text-foreground">FP1 (Ch1)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
          <span className="text-foreground">FP2 (Ch2)</span>
        </div>
      </div>
    </div>
  );
};

export default PowerSpectrumChart; 